import { Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProjectConfig } from '../../../../modules/executions';
import PlatformSelection from './PlatformSelectionForm';
import MultiSelectDropdown from '../../../../ui/primitives/MultiSelectDropdown';

const toOptions = (arr = []) => arr.map((s) => ({ id: s, name: s }));

const ExecutionScopeForm = ({
  values,
  setFieldValue,
  isEditing = true,
  isCycleCompleted,
  isPlatformRequired = true,
}) => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [projectConfig, setProjectConfig] = useState({});
  const {
    platforms = [],
    subPlatforms = [],
    browsers = [],
    languages = [],
    geographies = [],
    cloudDevice = [],
  } = values;

  useEffect(() => {
    const fetchProjectData = async () => {
      setLoading(true);
      try {
        const response = await getProjectConfig(projectId);
        setProjectConfig(response);
      } catch (error) {
        console.error('Error fetching project config:', error);
      }
      setLoading(false);
    };
    fetchProjectData();
  }, [projectId]);

  const filterSelectedValues = (selectedValues, availableOptions) =>
    selectedValues?.filter((value) => availableOptions?.includes(value));

  const cloudDeviceOptions = [...(projectConfig?.cloud_device || [])];
  if (
    cloudDeviceOptions.includes('Others') &&
    projectConfig?.other_cloud_device
  ) {
    const index = cloudDeviceOptions.indexOf('Others');
    cloudDeviceOptions[index] = projectConfig.other_cloud_device;
  }

  const browserOptions = [...(projectConfig?.browsers || [])];
  if (
    browserOptions.includes('Others') &&
    projectConfig?.other_browsers?.length > 0
  ) {
    const index = browserOptions.indexOf('Others');
    browserOptions.splice(index, 1, ...projectConfig.other_browsers);
  }

  const renderMultiSelect = (options, selectedValues, field, placeholder) => {
    const filteredSelectedValues = filterSelectedValues(
      selectedValues,
      options
    );

    return (
      <Grid item xs={12} md={4}>
        {isEditing && !isCycleCompleted ? (
          <MultiSelectDropdown
            label={placeholder}
            name={field}
            options={toOptions(options)}
            selectedValues={filteredSelectedValues}
            onChange={(value) => setFieldValue(field, value)}
            placeholder={`Select ${placeholder.toLowerCase()}`}
            searchable={options.length > 6}
            showSelectAll
          />
        ) : (
          <div>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {placeholder}
            </Typography>
            <Typography variant="body1" className="form-static-text">
              {filteredSelectedValues?.length > 0
                ? filteredSelectedValues.join(', ')
                : 'None'}
            </Typography>
          </div>
        )}
      </Grid>
    );
  };

  return loading ? (
    <Typography className="text-center text-muted" variant="body2">
      Fetching execution scope...
    </Typography>
  ) : (
    <>
      <Typography variant="h6" className="mb-2">
        Execution Scope
      </Typography>
      <Grid container spacing={2}>
        <PlatformSelection
          platforms={filterSelectedValues(platforms, projectConfig?.platforms)}
          subPlatforms={filterSelectedValues(
            subPlatforms,
            projectConfig?.sub_platforms
          )}
          browsers={filterSelectedValues(browsers, browserOptions)}
          geographies={filterSelectedValues(geographies, projectConfig?.geographies)}
          isCycleCompleted={isCycleCompleted}
          projectConfig={projectConfig}
          isEditing={isEditing}
          setFieldValue={setFieldValue}
          isPlatformRequired={isPlatformRequired}
          browserOptions={browserOptions}
        />
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        {projectConfig?.cloud_device?.length > 0 &&
          renderMultiSelect(
            cloudDeviceOptions,
            cloudDevice,
            'cloudDevice',
            'Cloud Device Labs'
          )}
        {projectConfig?.multiple_languages === 'Yes' &&
          projectConfig?.languages?.length > 0 &&
          renderMultiSelect(
            projectConfig?.languages,
            languages,
            'languages',
            'Languages'
          )}
      </Grid>
    </>
  );
};

export default ExecutionScopeForm;
